<template>
    <div class="service-level-report">
        <div class="columns toolbar-box action-right">
            <div class="column is-3">
                <h1 class="title is-3 between-center">
                    <span>
                        <span>Service Level Report</span>
                    </span>
                </h1>
            </div>

            <div class="column is-4-tablet is-2-desktop">
                <o-field>
                    <o-select v-model="filters.team_id" placeholder="Select Team" @update:modelValue="fetchPaginatedData(true)" expanded>
                        <option value="">All teams</option>
                        <option v-for="team in teams" :value="team.id" :key="team.id">
                           {{ team.name }}
                        </option>
                    </o-select>
                </o-field>
            </div>
            <div class="column is-4-tablet is-2-desktop">
                <div class="field">
                    <datepicker v-model="filters.from"
                                input-class="input is-fullwidth"
                                placeholder="Week ending from"
                                @update:modelValue="fetchPaginatedData(true)"
                                :is-time-picker="true"
                    />
                </div>
            </div>
            <div class="column is-4-tablet is-2-desktop">
                <datepicker v-model="filters.to"
                            input-class="input is-fullwidth"
                            placeholder="Week ending to"
                            @update:modelValue="fetchPaginatedData(true)"
                            :is-time-picker="true"
                />
            </div>
        </div>
        <div class="columns toolbar-box action-right">
            <div class="column is-4-tablet is-2-desktop is-offset-3">
                <div class="field">
                    <datepicker v-model="filters.converted_from"
                                input-class="input is-fullwidth"
                                placeholder="Converted from"
                                @update:modelValue="fetchPaginatedData(true)"
                                :is-time-picker="true"
                    />
                </div>
            </div>
            <div class="column is-4-tablet is-2-desktop">
                <datepicker v-model="filters.converted_to"
                            input-class="input is-fullwidth"
                            placeholder="Converted to"
                            @update:modelValue="fetchPaginatedData(true)"
                            :is-time-picker="true"
                />
            </div>
            <div class="column is-4-tablet is-2-desktop">
                <div class="field">
                    <multicomplete v-model="projects" type="project" placeholder="Projects" @update:modelValue="fetchPaginatedData(true)" />
                </div>
            </div>
            <div class="column is-4-tablet is-2-desktop">
                <div class="field">
                    <o-checkbox v-model="viewAll" @update:modelValue="fetchPaginatedData(true)">View All</o-checkbox>
                </div>
            </div>
        </div>
        <div class="columns">
            <div class="column is-1-desktop">&nbsp;</div>
            <div class="column is-4-tablet is-2-desktop">
                <div class="field">
                    <multicomplete v-model="exclude_services" field-name="title" type="service" placeholder="Exclude Services" @input="fetchPaginatedData(true)" />
                </div>
            </div>
            <div class="column is-4-tablet is-2-desktop">
                <div class="field">
                    <multicomplete v-model="services" field-name="title" type="service" placeholder="Services" @input="fetchPaginatedData(true)" />
                </div>
            </div>

            <div class="column is-4-tablet is-2-desktop">
                <div class="field">
                    <multicomplete v-model="users" type="user" placeholder="Project manager" @input="fetchPaginatedData(true)" />
                </div>
            </div>
            <div class="column is-4-tablet is-2-desktop">
                <div class="field">
                    <multicomplete v-model="serviceUsers" type="user" placeholder="Assigned To" @input="fetchPaginatedData(true)" />
                </div>
            </div>
            <div class="column is-2 action-box">
                <div class="field">
                    <p v-if="isResetFiltersButtonVisible" class="control">
                        <a @click.prevent="resetFilters" class="button is-info"><span>Reset filters</span></a>
                    </p>
                </div>
            </div>
        </div>
        <div class="columns">
            <div class="column is-1-desktop">&nbsp;</div>
            <div class="column is-4-tablet is-4-desktop">
                <o-field>
                    <autocomplete-select
                            v-model="filters.company_id"
                            :custom="{placeholder: 'Company'}"
                            type="company"
                            @update:modelValue="fetchPaginatedData(true)"
                    />
                </o-field>
            </div>
            <div class="column is-4-tablet is-2-desktop">
                <o-field>
                    <o-select
                            v-model="filters.borough"
                            :expanded="true"
                            placeholder="Borough"
                            @update:modelValue="fetchPaginatedData(true)"
                    >
                        <option
                                v-for="(b, i) in boroughs"
                                :value="b.id"
                                :key="i"
                        >
                            {{ b.name }}
                        </option>
                    </o-select>
                </o-field>
            </div>
            <div class="column is-4-tablet is-2-desktop">
                <div class="field">
                    <multicomplete v-model="contacts" type="contact" placeholder="Clients" @update:modelValue="fetchPaginatedData(true)" />
                </div>
            </div>
        </div>
        <br>
        <o-table
                class="fullwidth-table is-responsive is-responsive-desktop"

                v-if="isTableVisible"
                :data="paginatedData"

                :bordered="table.bordered"
                :striped="table.striped"
                :narrowed="table.narrowed"
                :checkable="false"
                :mobile-cards="table.mobileCards"
                :paginated="table.paginated"
                :pagination-simple="table.paginationSimple"
                :per-page="table.perPage"
                :default-sort="table.defaultSort"

                :selected.sync="selected"
                :checked-rows="checkedRows"
                @update:checkedRows="$event => checkedRows = $event"

                :loading="loading"
                :total="total"
                backend-sorting
                backend-pagination
                @page-change="onPageChange"
                @sort="onSort"
        >
            <template>
                <template>
                    <o-table-column
                            v-for="col in tableColumns"
                            v-bind="col"
                            #default="{ row }"
                            @click.native="openDetailsModal(row)"
                            :field="col.field"
                            :label="col.field === 'actions' ? '' : col.label"
                            :sortable="col.sortable"
                    >
                        <span v-if="col.field === 'borough'">{{ $filters.valueOrDefault(boroughNameById(row[col.field]), '-') }}</span>
                        <router-link v-else-if="col.field === 'project_id'" :to="{ name: 'projects.show', params: { slug: row.project_id } }">
                            {{ row.project_id }}
                        </router-link>
                        <router-link v-else-if="col.field === 'project_name'" :to="{ name: 'projects.show', params: { slug: row.project_id } }">
                            {{ $filters.valueOrDefault(row.project_name,'-') }}
                        </router-link>
                        <span v-else-if="col.field === 'amount'">
                            {{ $filters.currency(row.amount) }}
                        </span>
                        <span v-else-if="col.field === 'avgTimelog'">
                            {{ $filters.toFixed($filters.valueOrDefault(row.avgTimelog, '-'), 2) }}
                        </span>
                        <span v-else-if="col.field === 'daysAvg'">
                            {{ Number(row.daysAvg).toFixed(2) }}
                        </span>
                        <span v-else>
                            {{ $filters.valueOrDefault(row[col.field], '-') }}
                        </span>
                    </o-table-column>
                </template>
            </template>
            <template #footer>
                <template v-for="(col, idx) in tableColumns">
                    <th v-if="col.field === 'amount'" label="Days">
                        {{ totalsColumns.amountLabel }}
                    </th>
                    <th v-else-if="col.field === 'days'" label="Days">
                        {{ totalsColumns.days }}
                    </th>
                    <th v-else-if="col.field === 'daysAvg'" label="Avg Days">
                        {{ totalsColumns.daysAvg }}
                    </th>
                    <th v-else-if="idx === 0" >Totals / Avg</th>
                    <th v-else> </th>
                </template>
            </template>
          <template #empty>
            <div class="has-text-centered">
              This table is empty!
            </div>
          </template>
        </o-table>

       <o-modal :can-cancel="['escape', 'outside', 'button']" scroll="keep" v-model:active="isDetailsModalActive" has-modal-card width="100%">
            <service-level-details-report :row="selectedRow" :filtersProp="filters" />
        </o-modal>
    </div>
</template>

<script>
    import Datepicker from './../../../components/Datepicker'
    import Table from './../../../mixins/Table'
    import { mapGetters } from 'vuex'
    import ServiceLevelDetailsReport from './ServiceLevelDetailsReport'

    export default {
        mixins: [Table],

        components: {
            Datepicker,
            ServiceLevelDetailsReport,
        },

        computed: {
            ...mapGetters([
                'boroughs',
                'boroughNameById',
                'teamNameById',
                'teams',
                'workTypeNamesByIds',
            ]),
            totalsColumns() {
                const currency = this.$filters.currency
                const data = this.paginatedData,
                totalsColumns = { amount: 0, amountLabel: '', count: 0, days: 0, daysAvg: 0, },
                perPage = this.table.perPage || 0,
                start = 0,
                end = perPage
                for (let idx = start; idx < end && idx < data.length; idx++) {
                    totalsColumns.count++
                    const daysAvg = parseInt(data[idx].daysAvg) || 0
                    const amount = parseInt(data[idx].amount) || 0
                    totalsColumns.amount += amount
                    totalsColumns.days += parseInt(data[idx].days) || 0
                    totalsColumns.daysAvg += daysAvg
                }
                totalsColumns.amountLabel = currency(totalsColumns.amount)
                totalsColumns.daysAvg = Number(totalsColumns.daysAvg/(totalsColumns.count || 1)).toFixed(2)
                return totalsColumns
            },
       },

        data() {
            return {
                contacts: [],
                exclude_services: [],
                filters: {
                    contacts: [],
                    converted_from: null,
                    converted_to: null,
                    exclude_services: [],
                    from: null,
                    projects: [],
                    team_id: null,
                    to: null,
                    services: [],
                    serviceUsers: [],
                    users: [],
                },
                isDetailsModalActive: false,
                items: [],
                projects: [],
                selectedRow: {},
                services: [],
                serviceUsers: [],
                table: {
                    defaultSort: ['daysAvg', 'desc'],
                    perPage: 10,
                    perPageOptions: [5, 10, 15, 25, 50],
                },
                tableName: 'service-level',
                urlFilter: { enabled: false },
                users: [],
                viewAll: false,
            }
        },

        methods: {
            fetchData(params) {
                return axios.get(`/api/reports/service-level-report`, { params })
                    .then(res => {
                        this.items = res.data.data
                        return res
                    })
            },

            itemsByIds() {
                return this.items
            },

            openDetailsModal(row) {
                this.selectedRow = row
                this.isDetailsModalActive = true
            },
        },

        mounted() {
            this.fetchPaginatedData()
        },

        watch: {
            filters: {
                deep: true,
                handler() {
                    this.fetchPaginatedData()
                }
            },
            viewAll: {
                immediate: true,
                handler() {
                    this.table.paginated = ! this.viewAll

                    this.fetchPaginatedData()
                }
            },

            'filters.contacts'(v) { if (v.length === 0 && this.contacts.length) this.contacts = [] },
            'filters.exclude_services'(v) { if (v.length === 0 && this.exclude_services.length) this.exclude_services = [] },
            'filters.projects'(v) { if (v.length === 0 && this.projects.length) this.projects = [] },
            'filters.services'(v) { if (v.length === 0 && this.services.length) this.services = [] },
            'filters.serviceUsers'(v) { if (v.length === 0 && this.serviceUsers.length) this.serviceUsers = [] },
            'filters.users'(v) { if (v.length === 0 && this.users.length) this.users = [] },

            "contacts"(value) {
              this.filters.contacts = value
            },
            "exclude_services"(value) {
              this.filters.exclude_services = value
            },
            "projects"(value) {
              this.filters.projects = value
            },
            "services"(value) {
              this.filters.services = value
            },
            "serviceUsers"(value) {
              this.filters.serviceUsers = value
            },
            "users"(value) {
              this.filters.users = value
            },
        },
    }
</script>
<style>
.service-level-report .animation-content {
height: 100%;
padding-top: 64px;
width: 100%; }

</style>
